import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { adminApi, getAdmin } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Lock } from "lucide-react";

const TRANSITIONS = {
  setup: ["open"],
  open: ["closed", "setup"],
  closed: ["open", "frozen"],
  frozen: [],
};

const STATUS_STYLE = {
  setup: "bg-slate-100 text-slate-700 border-slate-300",
  open: "bg-emerald-50 text-emerald-800 border-emerald-200",
  closed: "bg-amber-50 text-amber-900 border-amber-200",
  frozen: "bg-blue-50 text-blue-800 border-blue-200",
};

export default function Elections() {
  const admin = getAdmin();
  const isOwner = admin?.role === "owner";
  const [elections, setElections] = useState([]);
  const [munis, setMunis] = useState([]);
  const [offices, setOffices] = useState([]);
  const [newMuni, setNewMuni] = useState("");
  const [newElection, setNewElection] = useState({ municipality_id: "", election_year: "", label: "", voting_opens_at: "", voting_closes_at: "" });
  const [newOffice, setNewOffice] = useState({ election_id: "", type: "councillor", label: "", ward_label: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    adminApi.get("/admin/elections").then((r) => setElections(r.data));
    adminApi.get("/admin/municipalities").then((r) => setMunis(r.data));
    adminApi.get("/admin/offices").then((r) => setOffices(r.data));
  };
  useEffect(load, []);

  const muniName = (id) => munis.find((m) => m.id === id)?.name || id;

  const setStatus = async (electionId, status) => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/elections/${electionId}/status`, { status });
      toast.success(`Election status → ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status change failed");
    } finally {
      setBusy(false);
    }
  };

  const createMuni = async () => {
    try {
      await adminApi.post("/admin/municipalities", { name: newMuni });
      toast.success("Municipality created");
      setNewMuni("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const createElection = async () => {
    try {
      await adminApi.post("/admin/elections", {
        ...newElection,
        election_year: parseInt(newElection.election_year, 10),
        voting_opens_at: new Date(newElection.voting_opens_at).toISOString(),
        voting_closes_at: new Date(newElection.voting_closes_at).toISOString(),
      });
      toast.success("Election created (status: setup)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const createOffice = async () => {
    try {
      await adminApi.post("/admin/offices", { ...newOffice, ward_label: newOffice.ward_label || null });
      toast.success("Office created");
      setNewOffice({ election_id: newOffice.election_id, type: "councillor", label: "", ward_label: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-elections-title">Elections</h1>
        {isOwner && (
          <div className="flex gap-2">
            <Dialog>
              <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="create-municipality-button"><Plus className="h-4 w-4 mr-1.5" /> Municipality</Button></DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle className="font-heading">New municipality</DialogTitle></DialogHeader>
                <Label>Name</Label>
                <Input value={newMuni} onChange={(e) => setNewMuni(e.target.value)} placeholder="e.g. City of Peterborough" data-testid="municipality-name-input" />
                <Button onClick={createMuni} disabled={newMuni.length < 2} data-testid="municipality-create-submit">Create</Button>
              </DialogContent>
            </Dialog>
            <Dialog>
              <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="create-election-button"><Plus className="h-4 w-4 mr-1.5" /> Election</Button></DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle className="font-heading">New election</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Municipality</Label>
                    <Select value={newElection.municipality_id} onValueChange={(v) => setNewElection((f) => ({ ...f, municipality_id: v }))}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-white">{munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Year</Label><Input type="number" className="mt-1.5" value={newElection.election_year} onChange={(e) => setNewElection((f) => ({ ...f, election_year: e.target.value }))} /></div>
                    <div><Label>Label</Label><Input className="mt-1.5" value={newElection.label} onChange={(e) => setNewElection((f) => ({ ...f, label: e.target.value }))} placeholder="2026 Municipal Election" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Voting opens</Label><Input type="datetime-local" className="mt-1.5" value={newElection.voting_opens_at} onChange={(e) => setNewElection((f) => ({ ...f, voting_opens_at: e.target.value }))} /></div>
                    <div><Label>Voting closes</Label><Input type="datetime-local" className="mt-1.5" value={newElection.voting_closes_at} onChange={(e) => setNewElection((f) => ({ ...f, voting_closes_at: e.target.value }))} /></div>
                  </div>
                  <Button onClick={createElection} disabled={!newElection.municipality_id || !newElection.election_year || !newElection.label || !newElection.voting_opens_at || !newElection.voting_closes_at}>Create</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <div className="space-y-5">
        {elections.map((e) => {
          const eOffices = offices.filter((o) => o.election_id === e.id);
          return (
            <Card key={e.id} data-testid={`election-card-${e.election_year}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="font-heading font-semibold text-lg">{e.label} · {muniName(e.municipality_id)}</h2>
                    <p className="text-xs text-muted-foreground font-mono mt-1">
                      {e.voting_opens_at} → {e.voting_closes_at}
                    </p>
                  </div>
                  <Badge variant="outline" className={STATUS_STYLE[e.status]} data-testid={`election-status-${e.election_year}`}>{e.status}</Badge>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {isOwner ? (
                    TRANSITIONS[e.status].map((next) => (
                      <Button
                        key={next}
                        size="sm"
                        variant={next === "open" ? "default" : "outline"}
                        disabled={busy}
                        onClick={() => setStatus(e.id, next)}
                        data-testid={`election-set-${next}-${e.election_year}`}
                      >
                        {next === "open" ? "Open declarations" : next === "closed" ? "Close declarations" : next === "frozen" ? "Freeze (evidence)" : "Back to setup"}
                      </Button>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Only the owner can change election status</p>
                  )}
                  {e.status === "frozen" && <p className="text-xs text-muted-foreground">Frozen elections are immutable (evidence preservation).</p>}
                </div>

                <div className="mt-5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Offices ({eOffices.length})</p>
                  <div className="flex flex-wrap gap-1.5">
                    {eOffices.map((o) => (
                      <Badge key={o.id} variant="outline" className="text-xs">{o.label}</Badge>
                    ))}
                  </div>
                  {isOwner && (
                    <div className="flex flex-wrap items-end gap-2 mt-4">
                      <div>
                        <Label className="text-xs">Type</Label>
                        <Select value={newOffice.election_id === e.id ? newOffice.type : "councillor"} onValueChange={(v) => setNewOffice((f) => ({ ...f, election_id: e.id, type: v }))}>
                          <SelectTrigger className="w-32 mt-1 bg-white"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">
                            {["mayor", "councillor", "trustee", "other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Label</Label>
                        <Input className="w-52 mt-1" placeholder="Councillor — Ward 9" value={newOffice.election_id === e.id ? newOffice.label : ""} onChange={(ev) => setNewOffice((f) => ({ ...f, election_id: e.id, label: ev.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-xs">Ward label</Label>
                        <Input className="w-32 mt-1" placeholder="Ward 9" value={newOffice.election_id === e.id ? newOffice.ward_label : ""} onChange={(ev) => setNewOffice((f) => ({ ...f, election_id: e.id, ward_label: ev.target.value }))} />
                      </div>
                      <Button size="sm" variant="outline" onClick={createOffice} disabled={newOffice.election_id !== e.id || !newOffice.label}>Add office</Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
